import type { ChangeEvent } from "react";

import type { PlayerFamilyProfileOption } from "../lib/api";
import { lowestProfileId } from "../lib/playerFamilyProfile";
import type { PlayerProfileScope } from "../lib/usePlayerProfileScope";

interface PlayerFamilyProfileSwitcherProps {
  familyProfiles: PlayerFamilyProfileOption[];
  activeProfileId: number | null | undefined;
  onChange: (profileId: number) => void;
  className?: string;
  /** Texto sobre el selector. «Jugando como» en todas las pantallas (Eduardo
   * 2026-10-01): es el nombre que usan la bienvenida y la pista de la cuenta. */
  label?: string;
  /** Mientras se guarda algo del hijo actual (p. ej. su foto) no se cambia. */
  disabled?: boolean;
}

export default function PlayerFamilyProfileSwitcher({
  familyProfiles,
  activeProfileId,
  onChange,
  className = "",
  label = "Jugando como",
  disabled = false,
}: PlayerFamilyProfileSwitcherProps) {
  if (familyProfiles.length <= 1) return null;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextId = Number(event.target.value);
    if (Number.isFinite(nextId) && nextId > 0) {
      onChange(nextId);
    }
  };

  // Sin perfil elegido todavía, el mismo que usa el servidor: el de id más bajo.
  const selected = activeProfileId ?? lowestProfileId(familyProfiles) ?? "";

  return (
    <div className={`player-family-switcher ${className}`.trim()}>
      <label>
        <span>{label}</span>
        <select value={selected} onChange={handleChange} disabled={disabled} aria-label="Cambiar perfil familiar">
          {familyProfiles.map((option) => (
            <option key={option.id} value={option.id}>
              {option.display_name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

interface PlayerProfileScopeSwitcherProps {
  scope: PlayerProfileScope;
  /** Pantalla de un torneo: solo los hijos inscritos en él. */
  tournament?: boolean;
  className?: string;
  disabled?: boolean;
}

/** Selector de perfil de la cuenta familiar ligado a `usePlayerProfileScope`:
 * solo aparece con 2 perfiles o más para elegir en esta pantalla. */
export function PlayerProfileScopeSwitcher({
  scope,
  tournament = false,
  className = "",
  disabled = false,
}: PlayerProfileScopeSwitcherProps) {
  if (scope.profiles.length < 2) return null;
  return (
    <div
      className={`player-family-switcher-shell${tournament ? " player-tournament-profile-bar" : ""} ${className}`.trim()}
    >
      <PlayerFamilyProfileSwitcher
        familyProfiles={scope.profiles}
        activeProfileId={scope.profileId}
        onChange={scope.setProfileId}
        label="Jugando como"
        disabled={disabled}
      />
    </div>
  );
}
